import PageHeader from "@/components/shared/PageHeader";
import CTASection from "@/components/shared/CTASection";
import LastReviewedBanner from "@/components/shared/LastReviewedBanner";
import {
  ETHICAL_AI_LIFECYCLE,
  RAI_ADOPTION_FRAMEWORK,
  UN_AI_ARCHITECTURE,
  AI_FOOTPRINT,
  CARE_PRINCIPLES,
  PORTFOLIO_LAST_REVIEWED,
} from "@/data/content";
import { Leaf, Globe, Shield } from "lucide-react";

export default function ResponsibleAIData() {
  return (
    <>
      <PageHeader
        overline="Responsible AI and Data"
        title="Responsible AI for climate modelling, disaster readiness and public value decision making."
        lead="Guidance for using AI, data, and advanced analytics in climate and disaster contexts with transparency, fairness, human oversight, sovereignty, and accountable governance."
        image="/ethical-ai-hero.jpg"
      />

      {/* Opportunity vs Risk */}
      <section className="ccdr-section">
        <div className="ccdr-container">
          <LastReviewedBanner date={PORTFOLIO_LAST_REVIEWED} source="Reflects UN Global Digital Compact (2024), NIST AI RMF, ISO/IEC 42001, IEEE 7000 series" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">Opportunity</p>
              <h2 className="ccdr-h2 mb-6">Faster, broader, more inclusive.</h2>
              <div className="ccdr-prose">
                <p>
                  AI-driven weather models now produce skilful forecasts faster and at lower cost than traditional numerical
                  methods. Machine learning helps map exposure and vulnerability, and natural language systems help translate
                  technical warnings into the languages and formats communities actually use. The International Telecommunication
                  Union has highlighted how AI and connectivity mapping can extend early warnings to people beyond the reach
                  of conventional networks.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">Risk</p>
              <h2 className="ccdr-h2 mb-6">New fragility, if used carelessly.</h2>
              <div className="ccdr-prose">
                <p>
                  AI can encode bias into who gets warned and who is overlooked. It can fail silently in the rare, extreme
                  events that matter most. It carries a real and growing environmental footprint. And it can concentrate
                  decision making in opaque systems that communities cannot question.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lifecycle */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">A practical stance</p>
              <h2 className="ccdr-h2">Responsible AI is a design discipline, applied across the lifecycle.</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-6">
              <p className="ccdr-lead">
                Six stages, each connected to an established instrument — from problem framing through to retirement.
              </p>
            </div>
          </div>

          <ol className="space-y-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {ETHICAL_AI_LIFECYCLE.map((s) => (
              <li key={s.step} className="bg-white p-8 lg:p-10 grid grid-cols-12 gap-6">
                <div className="col-span-12 lg:col-span-2">
                  <span
                    className="text-4xl lg:text-5xl tabular-nums"
                    style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                  >
                    {s.step}
                  </span>
                </div>
                <div className="col-span-12 lg:col-span-10">
                  <h3 className="ccdr-h3 mb-3">{s.title}</h3>
                  <p className="text-[#4B5563] max-w-3xl">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* P7021 spotlight */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="ccdr-eyebrow mb-5">A particularly delicate case</p>
            <h2 className="ccdr-h2">P7021 spotlight: AI in long-term climate projection.</h2>
          </div>
          <div className="lg:col-span-8">
            <div className="ccdr-callout">
              <p className="ccdr-prose">
                <strong>IEEE P7021</strong> — the recommended practice for
                implementing ethical artificial intelligence in long-term climate projections towards sustainability —
                addresses exactly this. When AI helps project future climate, the assumptions, uncertainties, and value
                judgements inside the model must be made visible to the people who rely on the results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Responsible AI Adoption Framework — 7 stages */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-6">
              <p className="ccdr-eyebrow mb-5">Responsible AI Adoption Framework</p>
              <h2 className="ccdr-h2">From principle to a lifecycle leaders can hold to account.</h2>
            </div>
            <div className="lg:col-span-6 lg:pt-6">
              <p className="ccdr-lead">
                Seven stages tying recognised instruments — NIST AI RMF, ISO/IEC 42001, IEEE 7000 and P7021, CARE
                Principles — into a sequence an institution can actually apply.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {RAI_ADOPTION_FRAMEWORK.map((s) => (
              <div key={s.step} className="bg-white p-8">
                <p
                  className="text-3xl tabular-nums mb-3"
                  style={{ color: "var(--ccdr-accent-dark)", fontWeight: 300 }}
                >
                  {s.step}
                </p>
                <h3 className="ccdr-h3 mb-3">{s.title}</h3>
                <p className="text-sm text-[#4B5563]">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data and AI sovereignty (CARE deep) */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Globe className="w-9 h-9 mb-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow mb-5">Sovereignty</p>
            <h2 className="ccdr-h2 mb-6">{CARE_PRINCIPLES.title}.</h2>
            <p className="ccdr-prose">{CARE_PRINCIPLES.body}</p>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
              {CARE_PRINCIPLES.pillars.map((p) => (
                <div key={p.letter} className="bg-white p-7">
                  <span
                    className="text-5xl"
                    style={{ color: "var(--ccdr-accent)", fontWeight: 300 }}
                  >
                    {p.letter}
                  </span>
                  <h4
                    className="mt-3 mb-2 text-base"
                    style={{ color: "var(--ccdr-primary-dark)", fontWeight: 500 }}
                  >
                    {p.name}
                  </h4>
                  <p className="text-sm text-[#4B5563]">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The footprint of AI */}
      <section className="ccdr-section bg-[#F3F4F6] border-y border-[#E5E7EB]">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Leaf className="w-9 h-9 mb-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow mb-5">Footprint</p>
            <h2 className="ccdr-h2">{AI_FOOTPRINT.title}.</h2>
          </div>
          <div className="lg:col-span-7 ccdr-prose">
            <p>{AI_FOOTPRINT.body}</p>
          </div>
        </div>
      </section>

      {/* Rights and equity + AI assurance */}
      <section className="ccdr-section">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Shield className="w-8 h-8 mb-5" style={{ color: "var(--ccdr-primary)" }} />
            <p className="ccdr-eyebrow mb-5">Rights and equity</p>
            <h2 className="ccdr-h2 mb-5">Who is warned, who is heard.</h2>
            <p className="ccdr-prose">
              AI in resilience decisions touches rights — to information, to protection, to redress. UNESCO&apos;s
              Recommendation on the Ethics of AI and UNICEF&apos;s AI and Children guidance set out how to keep these
              rights in view, especially for children and other groups most exposed to climate and disaster risk.
            </p>
          </div>
          <div className="lg:col-span-6">
            <p className="ccdr-eyebrow mb-5">AI assurance</p>
            <h2 className="ccdr-h2 mb-5">Make responsible AI auditable.</h2>
            <p className="ccdr-prose">
              Assurance is what turns AI principles into something an audit can check. ISO/IEC 42001 supplies the
              management-system structure; the NIST AI RMF supplies the functions; IEEE provides the engineering
              practice; the EU AI Act and the Hiroshima AI Process supply external accountability. The Centre&apos;s
              Standards & Assurance hub maps how these instruments fit together.
            </p>
          </div>
        </div>
      </section>

      {/* UN AI governance architecture */}
      <section className="ccdr-section bg-[#053043] text-white">
        <div className="ccdr-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p
              className="text-xs uppercase tracking-[0.22em] mb-5"
              style={{ color: "var(--ccdr-accent)" }}
            >
              The new global AI governance layer
            </p>
            <h2 className="ccdr-h2 text-white">{UN_AI_ARCHITECTURE.title}.</h2>
          </div>
          <div className="lg:col-span-7 text-[#CBD5E1] leading-relaxed text-lg space-y-5">
            <p>{UN_AI_ARCHITECTURE.body}</p>
            <div className="border-l-4 border-[#D4A373] pl-5 text-[#E9C46A]" style={{ fontSize: "0.95rem" }}>
              {UN_AI_ARCHITECTURE.why_matters}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        overline="From principle to practice"
        title="Open the underlying standards, or start the AI assurance maturity model."
        primary={{ to: "/frameworks", label: "Open Standards & Assurance" }}
        secondary={{ to: "/resources", label: "Or start the maturity assessment" }}
      />
    </>
  );
}
